import { EqualizationPreviewTable } from "features/closings/tabs/ClosingTabEqualizations";
import { aggFuncForFeesBreakdown } from "features/fees/posted/PostedFeeChargeUtils";
import {
  useFeeOffsetsTrueUpBreakdownTableDataRetrieveSuspense,
  useFeeTrueUpBreakdownForEventTableDataRetrieveSuspense,
  useFundFamilySubEventListSuspense,
} from "gen/endpoints/fund-family-event/fund-family-event";
import { useSecurityOwnershipCancelCorrectListSuspense } from "gen/endpoints/security-ownership/security-ownership";
import { FundFamilySubEventTypeEnum } from "gen/model/fundFamilySubEventTypeEnum";
import { SecurityOwnershipCancelCorrectReadData } from "gen/model/securityOwnershipCancelCorrectReadData";
import React from "react";
import { useTranslation } from "react-i18next";
import {
  ContentBlock,
  ContentBlockBody,
  ContentBlockGroup,
} from "shared/components/content/ContentBlock";
import Descriptions from "shared/components/descriptions/Descriptions";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";
import { formatIsoDateToDisplay } from "shared/utils/DateUtils";

export const CommitmentCancelCorrectHistoricalPage: React.FC = () => {
  const { t } = useTranslation(["commitment", "common", "fees"]);
  const { fundFamilyEventId, commitmentId, fundFamilyId } =
    useRequiredStringParams();
  const { data: cancelCorrectDataList } =
    useSecurityOwnershipCancelCorrectListSuspense(fundFamilyId, commitmentId);

  const { data: catchUpBreakdownTableData } =
    useFeeTrueUpBreakdownForEventTableDataRetrieveSuspense(
      fundFamilyId,
      fundFamilyEventId,
    );

  const { data: offsetsTrueUpBreakdownTableData } =
    useFeeOffsetsTrueUpBreakdownTableDataRetrieveSuspense(
      fundFamilyId,
      fundFamilyEventId,
    );

  const cancelCorrectData: SecurityOwnershipCancelCorrectReadData | undefined =
    cancelCorrectDataList.find(
      (event: SecurityOwnershipCancelCorrectReadData) =>
        event.event_id === fundFamilyEventId,
    );

  const { data: subEvents } = useFundFamilySubEventListSuspense(
    fundFamilyId,
    fundFamilyEventId,
  );

  const equalizationSubEvent = subEvents.find(
    (subEvent) => subEvent.type === FundFamilySubEventTypeEnum.equalization,
  );

  return (
    <PageContent contentWidth="wide">
      <PageHeader
        title={t("commitment:cancelCorrect.cancelCorrect")}
        backButton={{
          title: t("common:commitment"),
          path: rootRoute({})
            .fundFamilies({})
            .fundFamily({ fundFamilyId })
            .commitments({})
            .commitment({ commitmentId })
            .profile({}).$,
        }}
      />
      <PageBody>
        <ContentBlockGroup>
          <ContentBlock>
            <ContentBlockBody variant="descriptions">
              <Descriptions>
                <Descriptions.Item label={t("common:effectiveDate")}>
                  {formatIsoDateToDisplay(cancelCorrectData?.effective_date)}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t("commitment:cancelCorrect.editClass.oldClass")}
                >
                  {cancelCorrectData?.previous_investor_class_name}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t("commitment:cancelCorrect.editClass.newClass")}
                >
                  {cancelCorrectData?.new_investor_class_name}
                </Descriptions.Item>
              </Descriptions>
            </ContentBlockBody>
          </ContentBlock>
        </ContentBlockGroup>
        <TableBox>
          <TableBoxHeader>
            <TableBoxTitle>
              {t("fees:trueUpPages.trueUpBreakdown")}
            </TableBoxTitle>
            <DataTableBoxActions />
          </TableBoxHeader>
          <TableBoxBody>
            <DataTable
              data={catchUpBreakdownTableData}
              aggFunc={aggFuncForFeesBreakdown}
            />
          </TableBoxBody>
        </TableBox>
        <TableBox>
          <TableBoxHeader>
            <TableBoxTitle>
              {t("fees:trueUpPages.equalizedOffsets")}
            </TableBoxTitle>
            <DataTableBoxActions />
          </TableBoxHeader>
          <TableBoxBody>
            <DataTable data={offsetsTrueUpBreakdownTableData} />
          </TableBoxBody>
        </TableBox>
        <TableBox>
          <TableBoxHeader>
            <TableBoxTitle>{t("common:equalization")}</TableBoxTitle>
            <DataTableBoxActions />
          </TableBoxHeader>
          <TableBoxBody>
            {equalizationSubEvent && (
              <EqualizationPreviewTable
                fundFamilyId={fundFamilyId}
                fundFamilyEventId={fundFamilyEventId}
                equalizationSubEvent={equalizationSubEvent}
                isCreateEqualizationLoading={false}
              />
            )}
          </TableBoxBody>
        </TableBox>
      </PageBody>
    </PageContent>
  );
};
